import {
  closestCorners,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import {
  Button,
  Card,
  Col,
  FloatingLabel,
  Form,
  Row,
  Spinner,
  Table,
  Modal,
} from "react-bootstrap";

import Select from "react-select";
import CustomOption from "utils/CustomOption";
import ActionButton from "components/common/ActionButton";
import { useFetchCategories } from "../../../../hooks/useFetchCategories";
import { useEffect, useState } from "react";
import { useFetchPages } from "hooks/useFetchPages";

const SortableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td
        {...listeners}
        {...attributes}
        style={{
          cursor: "grab",
          textAlign: "center",
          width: "20px",
          // paddingTop: "15px",
        }}
      >
        ☰
      </td>
      {children}
    </tr>
  );
};

const FooterCopyRightsModal = ({ show, block, onClose, onChange }) => {
  const {
    register,
    setValue,
    clearErrors,
    handleSubmit,
    control,
    trigger,
    watch,
    setError,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: block.settings,
  });

  const {
    fields: options,
    append: appendOptions,
    remove: removeOptions,
    move,
  } = useFieldArray({
    control,
    name: "options",
  });

  const { data: pagesData, isLoading: pagesDataLoading } = useFetchPages();

  let pagesDataOptions = pagesData?.data?.data;

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = options.findIndex((item) => item.id === active.id);
      const newIndex = options.findIndex((item) => item.id === over.id);
      move(oldIndex, newIndex);
    }
  };

  const submit = (data) => {
    onChange(data); // ✅ one controlled update
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Footer Copyrights</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="gx-2">
            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Name"
                  title="Name"
                  className="py-2"
                  isInvalid={!!errors.name}
                  {...register("name")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.name?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>

            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Copyright Text"
                  title="Copyright Text"
                  className="py-2"
                  isInvalid={!!errors.copyRightText}
                  {...register("copyRightText")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.copyRightText?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
          </Row>

          <h5 className="fs-10 mb-0 text-nowrap pb-2"> Page Url </h5>

          <Row className="gx-2 px-1">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={options.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <Table>
                  <thead className="bg-200">
                    <tr>
                      <th
                        scope="col"
                        className="w-0 fs-10 text-center"
                        style={{ width: "10px" }}
                      >
                        Order
                      </th>
                      <th scope="col" className="fs-10">
                        Options
                      </th>
                      <th
                        style={{ width: "10px", padding: "12px 15px" }}
                        scope="col"
                        className="fs-10 text-center"
                      >
                        Active
                      </th>
                      <th
                        scope="col"
                        style={{ padding: "9px", width: "10px" }}
                        className=" text-end"
                      >
                        <ActionButton
                          icon="plus"
                          variant="success"
                          title="Add"
                          className="px-1 py-0"
                          // disabled={options.length > 0}
                          onClick={() => appendOptions({})}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {options.map((item, index) => (
                      <SortableItem key={item.id} id={item.id}>
                        <td style={{ padding: "5px" }}>
                          <Form.Group controlId="formGroupName">
                            <Form.Select
                              aria-label="options"
                              isInvalid={!!errors.options?.[index]?.variantId}
                              {...register(`options[${index}].variantId`)}
                            >
                              <option value="">Select</option>
                              {pagesDataOptions?.map((d) => (
                                <option value={d.id}>{d.title}</option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              {errors.options?.[index]?.variantId?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </td>
                        <td style={{ padding: "5px" }} className="text-center">
                          <Form.Check
                            type="checkbox"
                            id="defaultSwitch"
                            className="mt-1"
                            // defaultChecked={!state}
                            isInvalid={!!errors.options?.[index]?.status}
                            {...register(`options[${index}].status`)}
                          />
                        </td>
                        <td style={{ padding: "5px" }} className="text-center">
                          <ActionButton
                            icon="trash-alt"
                            title="Delete"
                            variant="danger"
                            className="px-1 py-0"
                            onClick={() => removeOptions(index)}
                          />
                        </td>
                      </SortableItem>
                    ))}
                  </tbody>
                </Table>
              </SortableContext>
            </DndContext>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit(submit)}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FooterCopyRightsModal;

const flattenOptions = (nodes, level = 1) => {
  return nodes.reduce((acc, node) => {
    const label = `${"".repeat(level - 0)} ${node.name}`;
    acc.push({
      id: node.id,
      value: node.id,
      label,
      parent: node.parent,
      level,
    });

    if (node.children && node.children.length > 0) {
      acc.push(...flattenOptions(node.children, level + 1));
    }

    return acc;
  }, []);
};

const createHierarchy = (data) => {
  const lookup = {};
  const result = [];

  data?.forEach((item) => {
    lookup[item.id] = { ...item, children: [] };
  });

  data?.forEach((item) => {
    const { id, parent } = item;
    if (parent === 0) {
      result.push(lookup[id]);
    } else if (lookup[parent]) {
      lookup[parent].children.push(lookup[id]);
    }
  });

  return result;
};

const transformFlatToHierarchical = (flatOptions) => {
  const map = new Map();

  // Initialize all categories in the map
  flatOptions.forEach((option) => {
    map.set(option.value, { ...option, children: [] });
  });

  const root = [];

  // Build hierarchical structure
  flatOptions.forEach((option) => {
    if (option.parent === 0) {
      root.push(map.get(option.value));
    } else {
      const parent = map.get(option.parent);
      if (parent) {
        parent.children.push(map.get(option.value));
      }
    }
  });

  return root;
};
